import React, { useRef } from "react";
import { Box, Dialog, DialogContent, DialogProps } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import TaxGuruIconButton from "./button/TaxGuruIconButton";
import { SxProps, SystemStyleObject } from "@mui/system";
import Paper, { PaperProps } from "@mui/material/Paper";
import Draggable from "react-draggable";
import useDialogPosition from "./hook/useDialogPosition";

export type TaxGuruDialogProps = Partial<Omit<DialogProps, "title">> & {
    title?: string | null;
    saveId?: string;
    children?: React.ReactNode;
    actions?: React.ReactNode;
    headerNode?: React.ReactNode;
    variant?: "forPage" | "forForm" | "confirm";
    draggable?: boolean;
    disableClosing?: boolean;
};

function PaperComponent({ id, ...other }: PaperProps) {
    const ref = useRef(null);
    const { position, savePosition } = useDialogPosition(id);
    return (
        <Draggable
            defaultPosition={position}
            onStop={(e, data) => savePosition({ x: data.x, y: data.y })}
            nodeRef={ref}
            handle="#taxGuruDialogDraggable"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper ref={ref} {...other} />
        </Draggable>
    );
}

const TaxGuruDialog = ({
    disableClosing,
    saveId,
    draggable,
    variant,
    onClose,
    title,
    children,
    actions,
    headerNode,
    sx,
    ...other
}: TaxGuruDialogProps) => {
    const theme = useTheme();

    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const localSx: SystemStyleObject<Theme> = typeof sx === "function" ? sx(theme) : (sx as SystemStyleObject<Theme>);
    const sxMerged: SxProps<Theme> = (theme) => ({
        ...((variant === "forPage" || variant === "forForm") && {
            "& .MuiPaper-root:first-of-type": { width: { xs: "100%", md: "700px" } },
        }),
        ...(variant === "confirm" && {
            "& .MuiPaper-root:first-of-type": { width: { xs: "100%", md: "500px" } },
        }),
        ...localSx,
    });

    const mayDrag = draggable && !fullScreen;
    return (
        <Dialog
            PaperComponent={mayDrag ? PaperComponent : undefined}
            PaperProps={{ id: saveId }}
            onClose={onClose}
            maxWidth={false}
            fullScreen={fullScreen}
            open={true}
            sx={sxMerged}
            {...other}
        >
            <DialogTitle
                sx={{
                    cursor: mayDrag ? "move" : "unset",
                }}
                id="taxGuruDialogDraggable"
            >
                <Stack justifyContent="space-between" alignItems="center" direction="row">
                    {title && (
                        <Typography sx={{ flexGrow: 1 }} variant="h5">
                            {title}
                        </Typography>
                    )}
                    {headerNode && <Box paddingRight={1}>{headerNode}</Box>}
                    {!disableClosing && (
                        <TaxGuruIconButton sx={{ marginLeft: "auto" }} size="small" onClick={(e: React.MouseEvent) => onClose?.(e, "escapeKeyDown")}>
                            <CloseIcon />
                        </TaxGuruIconButton>
                    )}
                </Stack>
            </DialogTitle>
            {children && (
                <DialogContent>
                    <Box sx={{ marginTop: 1 }}>{children} </Box>
                </DialogContent>
            )}
            {actions && <DialogActions>{actions}</DialogActions>}
        </Dialog>
    );
};

export default TaxGuruDialog;
